/**
 * Central API hook for all DGA (Dealer Generated Application) operations.
 * Every method wraps the @porchpass/api-client endpoint functions.
 */
import { DomainLandSituationType } from "@porchpass/api-client";
import { useCallback } from "react";
import { useDealer } from "src/hooks/useDealer";
import type {
  AdditionalIncome,
  Address,
  BorrowerData,
  Employment,
  Expense,
  FinancialAccount,
  HomeData,
  LandData,
  LenderConfig,
  PricingData,
} from "../types/applicationTypes";
import {
  mapAddressToApi,
  mapAssetToApi,
  mapBorrowerToApi,
  mapConsentToApi,
  mapDemographicsToApi,
  mapEmploymentToApi,
  mapEmploymentToUpdateApi,
  mapExpenseToApi,
  mapHomeToApi,
  mapIncomeToApi,
  mapLandToApi,
  mapPricingToSummaryApi,
  stripEmptyFields,
  stripEmptyFieldsForUpdate,
} from "../utils/apiModelAdapters";
import { getEmptyOptionalSectionsToSkip } from "../utils/optionalSectionSkip";

// --- API endpoint functions ---
import {
  deleteDealerPortalV1DealerIdBorrowerBorIdAddressAddId,
  deleteDealerPortalV1DealerIdBorrowerBorIdAssetAsstId,
  deleteDealerPortalV1DealerIdBorrowerBorIdEmploymentEmpId,
  deleteDealerPortalV1DealerIdBorrowerBorIdExpenseExpId,
  deleteDealerPortalV1DealerIdBorrowerBorIdIncomeIncId,
  // GET endpoints for loading
  getDealerPortalV1DealerIdApplicationAppId,
  getDealerPortalV1DealerIdApplicationAppIdApplicationLenders,
  getDealerPortalV1DealerIdApplicationAppIdHome,
  getDealerPortalV1DealerIdApplicationAppIdLand,
  // Application summary
  getDealerPortalV1DealerIdApplicationAppIdSummary,
  getDealerPortalV1DealerIdBorrowerBorId,
  getDealerPortalV1DealerIdBorrowerBorIdAddresses,
  getDealerPortalV1DealerIdBorrowerBorIdAssets,
  getDealerPortalV1DealerIdBorrowerBorIdDemographics,
  getDealerPortalV1DealerIdBorrowerBorIdEmployments,
  getDealerPortalV1DealerIdBorrowerBorIdExpenses,
  getDealerPortalV1DealerIdBorrowerBorIdIncomes,
  // Employees
  getDealerPortalV1DealerIdEmployees,
  // Lenders
  getDealerPortalV1DealerIdLenders,
  // Application details
  patchDealerPortalV1DealerIdApplicationsAppId,
  // Progress
  postDealerPortalV1DealerIdApplicationAppIdCalculateProgress,
  // Co-borrower
  postDealerPortalV1DealerIdApplicationAppIdCoborrower,
  // Signing (dealer-portal endpoints)
  postDealerPortalV1DealerIdApplicationAppIdEnvelope,
  // Land (DGA dealer-portal endpoints)
  postDealerPortalV1DealerIdApplicationAppIdLandAlreadyOwned,
  postDealerPortalV1DealerIdApplicationAppIdLandCurrentlyFinanced,
  postDealerPortalV1DealerIdApplicationAppIdLandFamily,
  postDealerPortalV1DealerIdApplicationAppIdLandLeasing,
  postDealerPortalV1DealerIdApplicationAppIdLandPurchasing,
  postDealerPortalV1DealerIdApplicationAppIdLandReservation,
  postDealerPortalV1DealerIdApplicationAppIdLenders,
  // Lock/Unlock
  postDealerPortalV1DealerIdApplicationAppIdLock,
  postDealerPortalV1DealerIdApplicationAppIdReopen,
  postDealerPortalV1DealerIdApplicationAppIdUnlock,
  postDealerPortalV1DealerIdApplicationBorIdSign,
  postDealerPortalV1DealerIdApplicationBorIdSigned,
  postDealerPortalV1DealerIdApplicationsAppIdLenderLenderIdSubmit,
  postDealerPortalV1DealerIdApplicationsStart,
  // Address CRUD
  postDealerPortalV1DealerIdBorrowerBorIdAddress,
  // Asset CRUD (DGA endpoints)
  postDealerPortalV1DealerIdBorrowerBorIdAsset,
  // Employment CRUD (DGA endpoints)
  postDealerPortalV1DealerIdBorrowerBorIdEmployment,
  // Expense CRUD (DGA endpoints)
  postDealerPortalV1DealerIdBorrowerBorIdExpense,
  // Income CRUD (DGA endpoints)
  postDealerPortalV1DealerIdBorrowerBorIdIncome,
  postV1DealerIdBorrowerBorIdSkipSection,
  // Home CRUD (DGA dealer-portal endpoints)
  putDealerPortalV1DealerIdApplicationAppIdHome,
  putDealerPortalV1DealerIdApplicationAppIdSummary,
  putDealerPortalV1DealerIdApplicationsAppIdPrimaryLenderLenderId,
  // Borrower
  putDealerPortalV1DealerIdBorrowerBorId,
  putDealerPortalV1DealerIdBorrowerBorIdAddressAddId,
  putDealerPortalV1DealerIdBorrowerBorIdAssetAsstIdUpdateAsset,
  putDealerPortalV1DealerIdBorrowerBorIdConsent,
  // Demographics
  putDealerPortalV1DealerIdBorrowerBorIdDemographics,
  putDealerPortalV1DealerIdBorrowerBorIdEmploymentEmpIdUpdateEmployment,
  putDealerPortalV1DealerIdBorrowerBorIdExpenseExpIdUpdateExpense,
  putDealerPortalV1DealerIdBorrowerBorIdIncomeIncIdUpdateIncome,
} from "@porchpass/api-client";

import type {
  DomainApplicationLenderConfiguration,
  DomainLender,
  DtoBorrower,
  DtoGetBorrowerAssetsResp,
  DtoGetBorrowerExpensesResp,
  DtoGetBorrowerIncomesResp,
  DtoGetBorrowersAddressesResp,
  DtoGetBorrowersEmploymentsResp,
  DtoGetDemographicsResp,
  DtoGetLandResp,
  DtoHome,
  DtoSignApplicationReq,
  DtoStartApplicationDraftReq,
  DtoUpdateApplicationDetails,
  DtoUpdateAssetReq,
  DtoUpdateExpenseReq,
  DtoUpdateIncomeReq,
} from "@porchpass/api-client";

function requireDealerId(dealerId: string | undefined): string {
  if (!dealerId) throw new Error("dealerId is required");
  return dealerId;
}

export function useDgaApi() {
  const { dealer_id: dealerId } = useDealer();

  // =========================================================================
  // 3A: Application Lifecycle
  // =========================================================================

  const createDraftApplication = useCallback(
    async (
      firstName: string,
      lastName: string,
      email?: string,
      phoneNumber?: string,
    ) => {
      const body: DtoStartApplicationDraftReq = {
        first_name: firstName,
        last_name: lastName,
        email: email || undefined,
        phone_number: phoneNumber || undefined,
      };
      return postDealerPortalV1DealerIdApplicationsStart(
        requireDealerId(dealerId),
        body,
      );
    },
    [dealerId],
  );

  const createDraftApplicationIgnoreDuplicate = useCallback(
    async (
      firstName: string,
      lastName: string,
      email?: string,
      phoneNumber?: string,
    ) => {
      const body: DtoStartApplicationDraftReq = {
        first_name: firstName,
        last_name: lastName,
        email: email || undefined,
        phone_number: phoneNumber || undefined,
        ignore_duplicate: true,
      };
      return postDealerPortalV1DealerIdApplicationsStart(
        requireDealerId(dealerId),
        body,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3B: Borrower Save Methods
  // =========================================================================

  const saveBorrower = useCallback(
    async (
      borrower: BorrowerData,
      borrowerId: string,
      dirtyFields?: Set<string>,
      ignoreDuplicateFields?: boolean,
    ) => {
      const fullBody = mapBorrowerToApi(borrower);

      // Only send dirty fields to avoid validation errors on untouched fields
      let body = fullBody as Record<string, unknown>;
      if (dirtyFields) {
        const FIELD_MAP: Record<string, keyof typeof fullBody> = {
          firstName: "first_name",
          lastName: "last_name",
          middleName: "middle_name",
          suffix: "suffix",
          email: "email",
          phone: "phone_number",
          citizenship: "citizenship_type",
          maritalStatus: "marital_status_type",
          dependentsAgesYears: "dependents_ages_years",
          hasBankruptcy: "bankruptcy_date",
          bankruptcyDate: "bankruptcy_date",
          employmentStatus: "employment_status",
          nearestRelativeName: "nearest_relative_name",
          nearestRelativeRelationship: "nearest_relative_relationship",
          nearestRelativePhone: "nearest_relative_phone_number",
        };
        const partial: Record<string, unknown> = {};
        for (const field of dirtyFields) {
          const key = field
            .replace("primaryBorrower.", "")
            .replace("coBorrower.", "");
          const apiKey = FIELD_MAP[key];
          if (apiKey) partial[apiKey] = fullBody[apiKey];
        }
        // Backend requires dependents_ages_years to be present (empty array = no
        // dependents). Include it on any borrower save so nil is never left in DB.
        if (Object.keys(partial).length > 0) {
          partial.dependents_ages_years = fullBody.dependents_ages_years ?? [];
        }
        body = partial;
      }

      body.ignore_duplicate = !!ignoreDuplicateFields;

      return putDealerPortalV1DealerIdBorrowerBorId(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const saveBorrowerConsent = useCallback(
    async (
      borrower: BorrowerData,
      borrowerId: string,
      dirtyFields?: Set<string>,
    ) => {
      const body = mapConsentToApi(borrower, dirtyFields);
      return putDealerPortalV1DealerIdBorrowerBorIdConsent(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const saveDemographics = useCallback(
    async (borrower: BorrowerData, borrowerId: string) => {
      const body = mapDemographicsToApi(borrower);
      return putDealerPortalV1DealerIdBorrowerBorIdDemographics(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3C: Address Methods
  // =========================================================================

  const createAddress = useCallback(
    async (borrowerId: string, address: Address) => {
      const body = mapAddressToApi(address);
      return postDealerPortalV1DealerIdBorrowerBorIdAddress(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const updateAddress = useCallback(
    async (borrowerId: string, addressServerId: string, address: Address) => {
      const body = mapAddressToApi(address);
      return putDealerPortalV1DealerIdBorrowerBorIdAddressAddId(
        requireDealerId(dealerId),
        borrowerId,
        addressServerId,
        body,
      );
    },
    [dealerId],
  );

  const deleteAddress = useCallback(
    async (borrowerId: string, addressServerId: string) => {
      return deleteDealerPortalV1DealerIdBorrowerBorIdAddressAddId(
        requireDealerId(dealerId),
        borrowerId,
        addressServerId,
        {},
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3D: Employment Methods
  // =========================================================================

  const createEmployment = useCallback(
    async (borrowerId: string, employment: Employment) => {
      const body = mapEmploymentToApi(employment);
      return postDealerPortalV1DealerIdBorrowerBorIdEmployment(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const updateEmployment = useCallback(
    async (borrowerId: string, empServerId: string, employment: Employment) => {
      const body = mapEmploymentToUpdateApi(employment);
      return putDealerPortalV1DealerIdBorrowerBorIdEmploymentEmpIdUpdateEmployment(
        requireDealerId(dealerId),
        borrowerId,
        empServerId,
        body,
      );
    },
    [dealerId],
  );

  const deleteEmployment = useCallback(
    async (borrowerId: string, empServerId: string) => {
      return deleteDealerPortalV1DealerIdBorrowerBorIdEmploymentEmpId(
        requireDealerId(dealerId),
        borrowerId,
        empServerId,
        {},
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3E: Asset (Financial Account) Methods
  // =========================================================================

  const createAsset = useCallback(
    async (borrowerId: string, account: FinancialAccount) => {
      const body = stripEmptyFields(mapAssetToApi(account));
      return postDealerPortalV1DealerIdBorrowerBorIdAsset(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const updateAsset = useCallback(
    async (
      borrowerId: string,
      assetServerId: string,
      account: FinancialAccount,
    ) => {
      const body = stripEmptyFieldsForUpdate(
        mapAssetToApi(account),
      ) as DtoUpdateAssetReq;
      return putDealerPortalV1DealerIdBorrowerBorIdAssetAsstIdUpdateAsset(
        requireDealerId(dealerId),
        borrowerId,
        assetServerId,
        body,
      );
    },
    [dealerId],
  );

  const deleteAsset = useCallback(
    async (borrowerId: string, assetServerId: string) => {
      return deleteDealerPortalV1DealerIdBorrowerBorIdAssetAsstId(
        requireDealerId(dealerId),
        borrowerId,
        assetServerId,
        {},
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3F: Income Methods
  // =========================================================================

  const createIncome = useCallback(
    async (borrowerId: string, income: AdditionalIncome) => {
      const body = stripEmptyFields(mapIncomeToApi(income));
      return postDealerPortalV1DealerIdBorrowerBorIdIncome(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const updateIncome = useCallback(
    async (
      borrowerId: string,
      incomeServerId: string,
      income: AdditionalIncome,
    ) => {
      const body = stripEmptyFieldsForUpdate(
        mapIncomeToApi(income),
      ) as DtoUpdateIncomeReq;
      return putDealerPortalV1DealerIdBorrowerBorIdIncomeIncIdUpdateIncome(
        requireDealerId(dealerId),
        borrowerId,
        incomeServerId,
        body,
      );
    },
    [dealerId],
  );

  const deleteIncome = useCallback(
    async (borrowerId: string, incomeServerId: string) => {
      return deleteDealerPortalV1DealerIdBorrowerBorIdIncomeIncId(
        requireDealerId(dealerId),
        borrowerId,
        incomeServerId,
        {},
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3G: Expense Methods
  // =========================================================================

  const createExpense = useCallback(
    async (borrowerId: string, expense: Expense) => {
      const body = stripEmptyFields(mapExpenseToApi(expense));
      return postDealerPortalV1DealerIdBorrowerBorIdExpense(
        requireDealerId(dealerId),
        borrowerId,
        body,
      );
    },
    [dealerId],
  );

  const updateExpense = useCallback(
    async (borrowerId: string, expenseServerId: string, expense: Expense) => {
      const body = stripEmptyFieldsForUpdate(
        mapExpenseToApi(expense),
      ) as DtoUpdateExpenseReq;
      return putDealerPortalV1DealerIdBorrowerBorIdExpenseExpIdUpdateExpense(
        requireDealerId(dealerId),
        borrowerId,
        expenseServerId,
        body,
      );
    },
    [dealerId],
  );

  const deleteExpense = useCallback(
    async (borrowerId: string, expenseServerId: string) => {
      return deleteDealerPortalV1DealerIdBorrowerBorIdExpenseExpId(
        requireDealerId(dealerId),
        borrowerId,
        expenseServerId,
        {},
      );
    },
    [dealerId],
  );

  const skipBorrowerSection = useCallback(
    async (borrowerId: string, section: "assets" | "incomes" | "expenses") => {
      return postV1DealerIdBorrowerBorIdSkipSection(
        requireDealerId(dealerId),
        borrowerId,
        section,
      );
    },
    [dealerId],
  );

  const skipEmptyOptionalSections = useCallback(
    async (borrower: BorrowerData, borrowerId: string) => {
      const sections = getEmptyOptionalSectionsToSkip(borrower);
      await Promise.all(
        sections.map((section) => skipBorrowerSection(borrowerId, section)),
      );
    },
    [skipBorrowerSection],
  );

  // =========================================================================
  // 3H: Home / Property Methods
  // =========================================================================

  const updateHome = useCallback(
    async (
      appId: string,
      home: HomeData,
      pricing: PricingData,
      propertyAddress?: Address,
    ) => {
      const body = mapHomeToApi(home, pricing, propertyAddress);
      return putDealerPortalV1DealerIdApplicationAppIdHome(
        requireDealerId(dealerId),
        appId,
        body,
      );
    },
    [dealerId],
  );

  // Save only the down-payment-source fields via the Home endpoint.
  // The summary endpoint's swagger spec lists these fields but the actual
  // backend struct (ApplicationSummaryUpdateReq) does not support them.
  const updateDownPaymentSource = useCallback(
    async (
      appId: string,
      downPaymentSource: string,
      downPaymentSourceOther: string,
    ) => {
      const body: Parameters<
        typeof putDealerPortalV1DealerIdApplicationAppIdHome
      >[2] = {
        down_payment_source_type:
          downPaymentSource !== ""
            ? (downPaymentSource as Parameters<
                typeof putDealerPortalV1DealerIdApplicationAppIdHome
              >[2]["down_payment_source_type"])
            : undefined,
        down_payment_source_other: downPaymentSourceOther || undefined,
      };
      return putDealerPortalV1DealerIdApplicationAppIdHome(
        requireDealerId(dealerId),
        appId,
        body,
      );
    },
    [dealerId],
  );

  const updateApplicationDetails = useCallback(
    async (appId: string, details: DtoUpdateApplicationDetails) => {
      return patchDealerPortalV1DealerIdApplicationsAppId(
        requireDealerId(dealerId),
        appId,
        details,
      );
    },
    [dealerId],
  );

  const getApplicationSummary = useCallback(
    async (appId: string) => {
      return getDealerPortalV1DealerIdApplicationAppIdSummary(
        requireDealerId(dealerId),
        appId,
      );
    },
    [dealerId],
  );

  const updateApplicationSummary = useCallback(
    async (appId: string, pricing: PricingData) => {
      const body = mapPricingToSummaryApi(pricing);
      return putDealerPortalV1DealerIdApplicationAppIdSummary(
        requireDealerId(dealerId),
        appId,
        body,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3I: Land Methods (dealer-portal endpoints)
  // =========================================================================

  const saveLand = useCallback(
    async (appId: string, land: LandData) => {
      const situation = land.landSituation;
      if (!situation) return;

      const body = mapLandToApi(land);

      switch (situation) {
        case DomainLandSituationType.LandSituationTypeAlreadyOwned:
          return postDealerPortalV1DealerIdApplicationAppIdLandAlreadyOwned(
            requireDealerId(dealerId),
            appId,
            body,
          );
        case DomainLandSituationType.LandSituationTypeCurrentlyFinanced:
          return postDealerPortalV1DealerIdApplicationAppIdLandCurrentlyFinanced(
            requireDealerId(dealerId),
            appId,
            body,
          );
        case DomainLandSituationType.LandSituationTypePurchasing:
          return postDealerPortalV1DealerIdApplicationAppIdLandPurchasing(
            requireDealerId(dealerId),
            appId,
            body,
          );
        case DomainLandSituationType.LandSituationTypeLeasing:
          return postDealerPortalV1DealerIdApplicationAppIdLandLeasing(
            requireDealerId(dealerId),
            appId,
            body,
          );
        case DomainLandSituationType.LandSituationTypeFamily:
          return postDealerPortalV1DealerIdApplicationAppIdLandFamily(
            requireDealerId(dealerId),
            appId,
            body as Parameters<
              typeof postDealerPortalV1DealerIdApplicationAppIdLandFamily
            >[2],
          );
        case DomainLandSituationType.LandSituationReservation:
          return postDealerPortalV1DealerIdApplicationAppIdLandReservation(
            requireDealerId(dealerId),
            appId,
            body as Parameters<
              typeof postDealerPortalV1DealerIdApplicationAppIdLandReservation
            >[2],
          );
      }
    },
    [dealerId],
  );

  // =========================================================================
  // 3J: Co-Borrower Creation
  // =========================================================================

  const createCoBorrower = useCallback(
    async (appId: string) => {
      return postDealerPortalV1DealerIdApplicationAppIdCoborrower(
        requireDealerId(dealerId),
        appId,
        {},
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3K: Lender Methods
  // =========================================================================

  const fetchLenders = useCallback(async () => {
    return getDealerPortalV1DealerIdLenders(requireDealerId(dealerId));
  }, [dealerId]);

  const saveApplicationLenders = useCallback(
    async (appId: string, lenders: LenderConfig[]) => {
      const payload = {
        lenders: lenders.map((l) => ({
          lender_id: l.lenderId as DomainLender,
          enabled: l.isPrimary ? true : l.enabled,
          auto_submit: l.autoSubmit ?? false,
        })),
      };

      return postDealerPortalV1DealerIdApplicationAppIdLenders(
        requireDealerId(dealerId),
        appId,
        payload,
      );
    },
    [dealerId],
  );

  const submitToLender = useCallback(
    async (appId: string, lenderId: string) => {
      return postDealerPortalV1DealerIdApplicationsAppIdLenderLenderIdSubmit(
        requireDealerId(dealerId),
        appId,
        lenderId,
      );
    },
    [dealerId],
  );

  const setPrimaryLender = useCallback(
    async (appId: string, lenderId: string) => {
      return putDealerPortalV1DealerIdApplicationsAppIdPrimaryLenderLenderId(
        requireDealerId(dealerId),
        appId,
        lenderId,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3L: Signature / DocuSign Methods
  // =========================================================================

  const createSigningEnvelope = useCallback(
    async (appId: string, sendViaEmail: boolean = false) => {
      return postDealerPortalV1DealerIdApplicationAppIdEnvelope(
        requireDealerId(dealerId),
        appId,
        { send_via_email: sendViaEmail },
      );
    },
    [dealerId],
  );

  const signApplication = useCallback(
    async (borrowerId: string, signReq: DtoSignApplicationReq) => {
      return postDealerPortalV1DealerIdApplicationBorIdSign(
        requireDealerId(dealerId),
        borrowerId,
        signReq,
      );
    },
    [dealerId],
  );

  const markSigned = useCallback(
    async (borrowerId: string) => {
      return postDealerPortalV1DealerIdApplicationBorIdSigned(
        requireDealerId(dealerId),
        borrowerId,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3M: Application Progress
  // =========================================================================

  const calculateProgress = useCallback(
    async (appId: string) => {
      return postDealerPortalV1DealerIdApplicationAppIdCalculateProgress(
        requireDealerId(dealerId),
        appId,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // 3N: Lock/Unlock Methods
  // =========================================================================

  const lockApplication = useCallback(
    async (appId: string, force = false) => {
      const options: RequestInit | undefined = force
        ? {
            body: JSON.stringify({ force: true }),
            headers: { "Content-Type": "application/json" },
          }
        : undefined;
      return postDealerPortalV1DealerIdApplicationAppIdLock(
        requireDealerId(dealerId),
        appId,
        options,
      );
    },
    [dealerId],
  );

  const unlockApplication = useCallback(
    async (appId: string) => {
      return postDealerPortalV1DealerIdApplicationAppIdUnlock(
        requireDealerId(dealerId),
        appId,
      );
    },
    [dealerId],
  );

  const reopenApplication = useCallback(
    async (appId: string) => {
      return postDealerPortalV1DealerIdApplicationAppIdReopen(
        requireDealerId(dealerId),
        appId,
      );
    },
    [dealerId],
  );

  const getApplication = useCallback(
    async (appId: string) => {
      return getDealerPortalV1DealerIdApplicationAppId(
        requireDealerId(dealerId),
        appId,
      );
    },
    [dealerId],
  );

  // =========================================================================
  // Part 7: Load Existing Application Data
  // =========================================================================

  const loadApplication = useCallback(
    async (appId: string) => {
      // First, get the application to find borrower IDs
      const appResponse = await getDealerPortalV1DealerIdApplicationAppId(
        requireDealerId(dealerId),
        appId,
      );

      if (appResponse.status !== 200) {
        throw new Error("Failed to load application");
      }
      const app = appResponse.data;
      const primaryBorrowerId = app.borrowers?.find((b) => b.is_primary)?.id;
      const coBorrowerIds =
        app.borrowers
          ?.filter((b) => !b.is_primary)
          .map((b) => b.id ?? "")
          .filter(Boolean) ?? [];

      if (!primaryBorrowerId) {
        throw new Error("Application has no primary borrower ID");
      }

      // Parallel GET calls for primary borrower data
      // Use allSettled so a failed call for one section doesn't
      // prevent successful calls from populating other sections.
      const settled = await Promise.allSettled([
        getDealerPortalV1DealerIdBorrowerBorId(
          requireDealerId(dealerId),
          primaryBorrowerId,
        ),
        getDealerPortalV1DealerIdBorrowerBorIdAddresses(
          requireDealerId(dealerId),
          primaryBorrowerId,
          {},
        ),
        getDealerPortalV1DealerIdBorrowerBorIdEmployments(
          requireDealerId(dealerId),
          primaryBorrowerId,
          {},
        ),
        getDealerPortalV1DealerIdBorrowerBorIdAssets(
          requireDealerId(dealerId),
          primaryBorrowerId,
          {},
        ),
        getDealerPortalV1DealerIdBorrowerBorIdIncomes(
          requireDealerId(dealerId),
          primaryBorrowerId,
          {},
        ),
        getDealerPortalV1DealerIdBorrowerBorIdExpenses(
          requireDealerId(dealerId),
          primaryBorrowerId,
          {},
        ),
        getDealerPortalV1DealerIdBorrowerBorIdDemographics(
          requireDealerId(dealerId),
          primaryBorrowerId,
        ),
        getDealerPortalV1DealerIdApplicationAppIdHome(
          requireDealerId(dealerId),
          appId,
          {},
        ),
        getDealerPortalV1DealerIdApplicationAppIdLand(
          requireDealerId(dealerId),
          appId,
        ),
        getDealerPortalV1DealerIdApplicationAppIdApplicationLenders(
          requireDealerId(dealerId),
          appId,
        ),
        getDealerPortalV1DealerIdApplicationAppIdSummary(
          requireDealerId(dealerId),
          appId,
        ),
      ]);

      // Primary borrower is required — abort if it failed
      if (settled[0].status === "rejected") {
        throw new Error(
          `Failed to load primary borrower: ${String(settled[0].reason)}`,
        );
      }
      const borrowerResp = settled[0].value;
      if (!borrowerResp || borrowerResp.status !== 200) {
        throw new Error(
          `Failed to load primary borrower: status ${borrowerResp?.status}`,
        );
      }

      // Extract fulfilled values for optional calls (undefined for failed calls)
      const v = <T>(r: PromiseSettledResult<T>): T | undefined =>
        r.status === "fulfilled" ? r.value : undefined;
      const addressesResp = v(settled[1]);
      const employmentsResp = v(settled[2]);
      const assetsResp = v(settled[3]);
      const incomesResp = v(settled[4]);
      const expensesResp = v(settled[5]);
      const demographicsResp = v(settled[6]);
      const homeResp = v(settled[7]);
      const landResp = v(settled[8]);
      const appLendersResp = v(settled[9]);
      const summaryResp = v(settled[10]);

      // Parallel GET calls for co-borrower data (if co-borrower exists)
      const firstCoBorrowerId = coBorrowerIds[0];
      let coBorrowerData:
        | {
            borrower: DtoBorrower;
            addresses?: DtoGetBorrowersAddressesResp;
            employments?: DtoGetBorrowersEmploymentsResp;
            assets?: DtoGetBorrowerAssetsResp;
            incomes?: DtoGetBorrowerIncomesResp;
            expenses?: DtoGetBorrowerExpensesResp;
            demographics?: DtoGetDemographicsResp;
          }
        | undefined;

      if (firstCoBorrowerId) {
        const coSettled = await Promise.allSettled([
          getDealerPortalV1DealerIdBorrowerBorId(
            requireDealerId(dealerId),
            firstCoBorrowerId,
          ),
          getDealerPortalV1DealerIdBorrowerBorIdAddresses(
            requireDealerId(dealerId),
            firstCoBorrowerId,
            {},
          ),
          getDealerPortalV1DealerIdBorrowerBorIdEmployments(
            requireDealerId(dealerId),
            firstCoBorrowerId,
            {},
          ),
          getDealerPortalV1DealerIdBorrowerBorIdAssets(
            requireDealerId(dealerId),
            firstCoBorrowerId,
            {},
          ),
          getDealerPortalV1DealerIdBorrowerBorIdIncomes(
            requireDealerId(dealerId),
            firstCoBorrowerId,
            {},
          ),
          getDealerPortalV1DealerIdBorrowerBorIdExpenses(
            requireDealerId(dealerId),
            firstCoBorrowerId,
            {},
          ),
          getDealerPortalV1DealerIdBorrowerBorIdDemographics(
            requireDealerId(dealerId),
            firstCoBorrowerId,
          ),
        ]);

        const coBorrowerResp = v(coSettled[0]);
        const coAddressesResp = v(coSettled[1]);
        const coEmploymentsResp = v(coSettled[2]);
        const coAssetsResp = v(coSettled[3]);
        const coIncomesResp = v(coSettled[4]);
        const coExpensesResp = v(coSettled[5]);
        const coDemographicsResp = v(coSettled[6]);

        if (coBorrowerResp) {
          coBorrowerData = {
            borrower: coBorrowerResp.data as DtoBorrower,
            addresses: coAddressesResp?.data as
              DtoGetBorrowersAddressesResp | undefined,
            employments: coEmploymentsResp?.data as
              DtoGetBorrowersEmploymentsResp | undefined,
            assets: coAssetsResp?.data as DtoGetBorrowerAssetsResp | undefined,
            incomes: coIncomesResp?.data as
              DtoGetBorrowerIncomesResp | undefined,
            expenses: coExpensesResp?.data as
              DtoGetBorrowerExpensesResp | undefined,
            demographics: coDemographicsResp?.data as
              DtoGetDemographicsResp | undefined,
          };
        }
      }

      return {
        application: app,
        primaryBorrowerId,
        coBorrowerIds,
        borrower: borrowerResp?.data,
        addresses: addressesResp?.data as
          DtoGetBorrowersAddressesResp | undefined,
        employments: employmentsResp?.data as
          DtoGetBorrowersEmploymentsResp | undefined,
        assets: assetsResp?.data as DtoGetBorrowerAssetsResp | undefined,
        incomes: incomesResp?.data as DtoGetBorrowerIncomesResp | undefined,
        expenses: expensesResp?.data as DtoGetBorrowerExpensesResp | undefined,
        demographics: demographicsResp?.data as
          DtoGetDemographicsResp | undefined,
        home: homeResp?.data as DtoHome | undefined,
        land: landResp?.data as DtoGetLandResp | undefined,
        appLenders: appLendersResp?.data as
          DomainApplicationLenderConfiguration[] | undefined,
        summary: summaryResp?.status === 200 ? summaryResp.data : undefined,
        coBorrowerData,
      };
    },
    [dealerId],
  );

  // =========================================================================
  // 3O: Employee Methods
  // =========================================================================

  const fetchEmployees = useCallback(
    async (params?: { offset?: number; limit?: number }) => {
      return getDealerPortalV1DealerIdEmployees(
        requireDealerId(dealerId),
        params,
      );
    },
    [dealerId],
  );

  return {
    // Application lifecycle
    createDraftApplication,
    createDraftApplicationIgnoreDuplicate,
    getApplication,
    loadApplication,
    // Borrower
    saveBorrower,
    saveBorrowerConsent,
    saveDemographics,
    // Address CRUD
    createAddress,
    updateAddress,
    deleteAddress,
    // Employment CRUD
    createEmployment,
    updateEmployment,
    deleteEmployment,
    // Asset CRUD
    createAsset,
    updateAsset,
    deleteAsset,
    // Income CRUD
    createIncome,
    updateIncome,
    deleteIncome,
    // Expense CRUD
    createExpense,
    updateExpense,
    deleteExpense,
    skipBorrowerSection,
    skipEmptyOptionalSections,
    // Home
    updateHome,
    updateDownPaymentSource,
    // Application details
    updateApplicationDetails,
    // Application summary
    getApplicationSummary,
    updateApplicationSummary,
    // Land
    saveLand,
    // Co-borrower
    createCoBorrower,
    // Lenders
    fetchLenders,
    saveApplicationLenders,
    submitToLender,
    setPrimaryLender,
    // Progress
    calculateProgress,
    // Signing
    createSigningEnvelope,
    signApplication,
    markSigned,
    // Lock/Unlock
    lockApplication,
    unlockApplication,
    reopenApplication,
    // Employees
    fetchEmployees,
  };
}
